// =====================================================================
// api.js - UNICUL loc cu apeluri HTTP din frontend
// =====================================================================
import axios from 'axios';
import { estPagePublique } from '../lib/pagesPubliques.js';
import { cheminRetour } from '../lib/retourApresLogin.js';

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 15000,
});

// Interceptor: daca API raspunde 401, redirect la /login
api.interceptors.response.use(
  res => res,
  err => {
    // M-011: /embauche/<token> este pagină PUBLICĂ (candidatul nu are cont) —
    // 401 pe /auth/me acolo nu trebuie să redirecționeze la /login.
    // Commande prête (13-10-2026) : /order/<token> aussi — le client Nespart
    // ouvre ses photos sans compte Zeus. Commandes prêtes v2 (16-10-2026) :
    // /client/<token> = page du client (ses commandes), sans compte non plus.
    // (Liste unique des pages publiques : lib/pagesPubliques.js, partagée avec App.jsx.)
    if (err.response?.status === 401 && window.location.pathname !== '/login'
        && !estPagePublique(window.location.pathname)) {
      // 23-10-2026 : la page demandée suit (ex. /accidents?scan=BS-01, autocollant scanné hors
      // connexion) — après la connexion, Login.jsx et App.jsx y ramènent (chemin interne seulement).
      const ici = cheminRetour(window.location.pathname + window.location.search);
      window.location.href = ici ? `/login?retour=${encodeURIComponent(ici)}` : '/login';
    }
    return Promise.reject(err);
  }
);

// ---------------------------------------------------------------------
// errMsg — message d'erreur lisible pour l'utilisateur.
// Les erreurs de validation (Zod) arrivent en
//   { error:'ValidationError', message:'Donnees invalides', details:[{path,message}] }
// Sans lire `details`, l'operateur (PDA) voit « Donnees invalides » et
// ne sait pas quel champ corriger.
// ---------------------------------------------------------------------
export function errMsg(err) {
  const d = err?.response?.data;
  if (Array.isArray(d?.details) && d.details.length) {
    const parts = d.details
      .map((x) => (x && x.message ? (x.path ? `${x.path} : ${x.message}` : x.message) : null))
      .filter(Boolean);
    if (parts.length) return parts.join(' · ');
  }
  return d?.message || err?.message || 'Erreur inconnue';
}

// ---------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------
export const auth = {
  login: (username, password) => api.post('/auth/login', { username, password }).then(r => r.data),
  logout: () => api.post('/auth/logout').then(r => r.data),
  me: () => api.get('/auth/me').then(r => r.data),
  // Mot de passe oublié (23-10-2026) : réponse identique pour tous ; le jeton voyage dans le corps, jamais dans l'adresse.
  motDePasseOublie: (identifiant) => api.post('/auth/mot-de-passe-oublie', { identifiant }).then(r => r.data),
  nouveauMotDePasse: (jeton, motDePasse) => api.post('/auth/mot-de-passe', { jeton, motDePasse }).then(r => r.data),
};

// ---------------------------------------------------------------------
// Users (Faza 1 complet)
// ---------------------------------------------------------------------
export const users = {
  list: (params) => api.get('/users', { params }).then(r => r.data),
  get: (id) => api.get(`/users/${id}`).then(r => r.data),
  create: (data) => api.post('/users', data).then(r => r.data),
  update: (id, data) => api.put(`/users/${id}`, data).then(r => r.data),
  toggleActive: (id, expectedUpdatedAt) =>
    api.patch(`/users/${id}/toggle-active`, { expectedUpdatedAt }).then(r => r.data),
  // Day 14 — masquer/afficher un user dans Présence + Toutes les feuilles
  toggleMask: (id, body = {}) =>
    api.patch(`/users/${id}/toggle-presence-mask`, body).then(r => r.data),
  // Fériés dans une période de contrat CDD (invite de découpe — 18-08-2026)
  contractFerieCheck: (start, end) =>
    api.get('/users/contract-ferie-check', { params: { start, end } }).then(r => r.data),
  // Photos carte d'identité recto/verso (19-08-2026) — jointes au mail contrats
  uploadCi: (id, side, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/users/${id}/ci/${side}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  deleteCi: (id, side) => api.delete(`/users/${id}/ci/${side}`).then(r => r.data),
  ciUrl: (id, side) => `/api/users/${id}/ci/${side}`,
  // Day 9d — password reset
  resetPassword: (id) => api.post(`/users/${id}/reset-password`).then(r => r.data),
  resetPasswordBulk: (excludeUserIds) => api.post('/users/reset-password-bulk', { excludeUserIds }).then(r => r.data),
  changeMyPassword: (currentPassword, newPassword) =>
    api.put('/users/me/password', { currentPassword, newPassword }).then(r => r.data),
};

// ---------------------------------------------------------------------
// Medical certificates (Day 10) — Justificatif médical (HR/admin only)
// ---------------------------------------------------------------------
export const medicalCerts = {
  list: (params) => api.get('/medical-certificates', { params }).then(r => r.data),
  get: (id) => api.get(`/medical-certificates/${id}`).then(r => r.data),
  create: (data) => api.post('/medical-certificates', data).then(r => r.data),
  update: (id, data) => api.put(`/medical-certificates/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/medical-certificates/${id}`).then(r => r.data),
  uploadFile: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/medical-certificates/${id}/files`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  deleteFile: (fileId) => api.delete(`/medical-certificates/files/${fileId}`).then(r => r.data),
  // Download/preview: navigare directă (cookie httpOnly trimis automat same-origin)
  fileUrl: (fileId, inline = true) =>
    `/api/medical-certificates/files/${fileId}/download${inline ? '?inline=1' : ''}`,
};

// ---------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------
// Day 9 — Dashboards (admin + operator)
export const dashboard = {
  getAdmin: () => api.get('/dashboard/admin').then((r) => r.data),
  getOperator: () => api.get('/dashboard/operator').then((r) => r.data),
  getCoverage: (params) => api.get('/dashboard/coverage', { params }).then((r) => r.data),
};

export const groups = {
  list: (params) => api.get('/groups', { params }).then(r => r.data),
  get: (id) => api.get('/groups/' + id).then(r => r.data),
  create: (data) => api.post('/groups', data).then(r => r.data),
  update: (id, data) => api.put('/groups/' + id, data).then(r => r.data),
  remove: (id) => api.delete('/groups/' + id).then(r => r.data),
  addMember: (id, userId, mode = 'PRIMARY') => api.post('/groups/' + id + '/members', { userId, mode }).then(r => r.data),
  removeMember: (id, userId, mode) => api.delete('/groups/' + id + '/members/' + userId, { params: mode ? { mode } : {} }).then(r => r.data),
  setPermissions: (id, permissions) => api.put('/groups/' + id + '/permissions', { permissions }).then(r => r.data),
};

// ---------------------------------------------------------------------
// Projects (Faza 2 - CRUD complet)
// ---------------------------------------------------------------------
export const projects = {
  list: (params) => api.get('/projects', { params }).then(r => r.data),
  get: (id) => api.get(`/projects/${id}`).then(r => r.data),
  create: (data) => api.post('/projects', data).then(r => r.data),
  update: (id, data) => api.put(`/projects/${id}`, data).then(r => r.data),
  toggleActive: (id, expectedUpdatedAt) =>
    api.patch(`/projects/${id}/toggle-active`, { expectedUpdatedAt }).then(r => r.data),
  createFromTemplate: (data) => api.post('/projects/from-template', data).then(r => r.data),
};

// ---------------------------------------------------------------------
// Tasks (Faza 2 - nested sub projects)
// ---------------------------------------------------------------------
export const tasks = {
  list: (projectId) => api.get(`/projects/${projectId}/tasks`).then(r => r.data),
  get: (projectId, taskId) => api.get(`/projects/${projectId}/tasks/${taskId}`).then(r => r.data),
  create: (projectId, data) => api.post(`/projects/${projectId}/tasks`, data).then(r => r.data),
  update: (projectId, taskId, data) => api.put(`/projects/${projectId}/tasks/${taskId}`, data).then(r => r.data),
  toggleActive: (projectId, taskId, expectedUpdatedAt) =>
    api.patch(`/projects/${projectId}/tasks/${taskId}/toggle-active`, { expectedUpdatedAt }).then(r => r.data),
  listTemplates: () => api.get('/projects/task-templates').then(r => r.data),
  // 22-10-2026 (demande Luca) : toutes les tâches de tous les projets, avec leur usage — réponse brute (blob + en-têtes)
  exportAll: (format = 'xlsx') => api.get('/projects/tasks/export', { params: { format }, responseType: 'blob', timeout: 120000 }),
};

// ---------------------------------------------------------------------
// Attendance (Faza 0 + Faza 3.0)
// ---------------------------------------------------------------------
export const attendance = {
  list: (params) => api.get('/attendance', { params }).then(r => r.data),
  get: (id) => api.get(`/attendance/${id}`).then(r => r.data),
  create: (data) => api.post('/attendance', data).then(r => r.data),
  addPartial: (data) => api.post('/attendance/partial', data).then(r => r.data),
  update: (id, data) => api.put(`/attendance/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/attendance/${id}`),
  // Faza 3.0
  matrix: (startDate, endDate, includeMasked = false) =>
    api.get('/attendance/matrix', { params: { startDate, endDate, ...(includeMasked ? { includeMasked: 'true' } : {}) } }).then(r => r.data),
  bulkDefaultTrav: (data) => api.post('/attendance/bulk-default-trav', data).then(r => r.data),
};


// Faza 3.1 - Timesheets MVP operator
export const timesheets = {
  getWeek: (weekStart) =>
    api.get('/timesheets/mes/semaine', { params: { weekStart } }).then(r => r.data),
  getJoursNonJustifies: () =>
    api.get('/timesheets/mes/jours-non-justifies').then(r => r.data),
  getProjectsTasks: () =>
    api.get('/timesheets/mes/projects-tasks').then(r => r.data),
  // FAZA B: createEntry (mereu CREATE entry nou cu ID unic).
  createEntry: (data) =>
    api.post('/timesheets/mes/entry', data).then(r => r.data),
  updateEntry: (id, data) =>
    api.put(`/timesheets/mes/entry/${id}`, data).then(r => r.data),
  removeEntry: (id) =>
    api.delete(`/timesheets/mes/entry/${id}`),
};

// ---------------------------------------------------------------------
// Timesheets Validation (Faza 3.2 - Admin per-entry, D-002 REV)
// ---------------------------------------------------------------------
export const timesheetsValidation = {
  listPending: () =>
    api.get('/timesheets/admin/pending').then(r => r.data),
  approve: (entryId) =>
    api.post(`/timesheets/admin/entries/${entryId}/approve`).then(r => r.data),
  reject: (entryId, reason) =>
    api.post(`/timesheets/admin/entries/${entryId}/reject`, { reason }).then(r => r.data),
};

// ---------------------------------------------------------------------
// Period Locks (Faza 3.3 - cloture mensuelle pentru facturare)
// + Faza 3.4 D-007: listActive() pentru banner UI (orice logged-in user)
// ---------------------------------------------------------------------
export const periodLocks = {
  list: (params) =>
    api.get('/admin/period-locks', { params }).then(r => r.data),
  create: (data) =>
    api.post('/admin/period-locks', data).then(r => r.data),
  unlock: (period, unlockReason) =>
    api.post('/admin/period-locks/' + period + '/unlock', { unlockReason }).then(r => r.data),
  getHistory: (period) =>
    api.get('/admin/period-locks/history/' + period).then(r => r.data),
  // Faza 3.4 D-007: endpoint info-only, accesibil oricarui user logat
  listActive: () =>
    api.get('/period-locks/active').then(r => r.data),
};

// ---------------------------------------------------------------------
// Overtime (Faza 3.5 Round 1)
// ---------------------------------------------------------------------
export const overtime = {
  // Operator self-service (permission: overtime.read.own)
  getBalance: () =>
    api.get('/overtime/me/balance').then(r => r.data),
  listMe: (params) =>
    api.get('/overtime/me', { params }).then(r => r.data),
  // Admin Ajouter (permission: overtime.create.all)
  addEntry: (data) =>
    api.post('/overtime', data).then(r => r.data),
  // Day 14 — opérateurs encodables (délégation: overtime.create.delegate OU .all)
  encodableUsers: () =>
    api.get('/overtime/encodable-users').then(r => r.data),
  updateEntry: (id, data) =>
    api.put('/overtime/' + id, data).then(r => r.data),
  deleteEntry: (id) =>
    api.delete('/overtime/' + id).then(r => r.data),
  // Admin tableau (permission: overtime.read.all)
  listAdminPeriod: (period) =>
    api.get('/overtime/admin/period/' + period).then(r => r.data),
  // Decisions (Day 6 / OT_DEC_V1)
  triggerDecisions: (body) =>
    api.post('/overtime/decisions/trigger', body).then(r => r.data),
  myPendingDecisions: (status) =>
    api.get('/overtime/decisions/my-pending', { params: status ? { status } : {} }).then(r => r.data),
  adminAllPendingDecisions: (status) =>
    api.get('/overtime/decisions/admin/all-pending', { params: status ? { status } : {} }).then(r => r.data),
  respondDecision: (id, body) =>
    api.post('/overtime/decisions/' + id + '/respond', body).then(r => r.data),
  adminForceDecision: (id, body) =>
    api.post('/overtime/decisions/' + id + '/admin-force', body).then(r => r.data),
  // Soldes opérateurs (Day 9) — permission: overtime.read.all
  adminBalances: (includeInactive = false) =>
    api.get('/overtime/admin/balances', { params: { includeInactive } }).then(r => r.data),
  adminGroupSettings: () =>
    api.get('/overtime/admin/group-settings').then(r => r.data),
  updateGroupSetting: (groupId, body) =>
    api.put('/overtime/admin/group-settings/' + groupId, body).then(r => r.data),
  adminListUserEntries: (userId, params) =>
    api.get('/overtime/admin/user/' + userId + '/entries', { params }).then(r => r.data),
};

// ---------------------------------------------------------------------
// Leaves (Faza 3.6 Round 1)
// ---------------------------------------------------------------------
export const leaves = {
  // Operator self-service
  submit: (data) => api.post('/leaves', data).then(r => r.data),
  listMe: (params) => api.get('/leaves/me', { params }).then(r => r.data),
  cancel: (id) => api.post('/leaves/' + id + '/cancel').then(r => r.data),
  getMyBalance: (year) =>
    year
      ? api.get('/leaves/me/balance/' + year).then(r => r.data)
      : api.get('/leaves/me/balance').then(r => r.data),
  // Admin
  listPending: () => api.get('/leaves/admin/pending').then(r => r.data),
  listAll: (params) => api.get('/leaves/admin/all', { params }).then(r => r.data),
  approve: (id) => api.post('/leaves/' + id + '/approve').then(r => r.data),
  reject: (id, decidedReason) =>
    api.post('/leaves/' + id + '/reject', { decidedReason }).then(r => r.data),
  adminDirect: (data) => api.post('/leaves/admin/direct', data).then(r => r.data),
  cancelApproved: (id) => api.post('/leaves/' + id + '/cancel-approved').then(r => r.data),
  restore: (id) => api.post('/leaves/' + id + '/restore').then(r => r.data),
  // JFR (17-08-2026) — fériés week-end : détection + crédit MANUEL (validation admin)
  jfrPending: () => api.get('/leaves/admin/jfr-pending').then(r => r.data),
  jfrCandidates: (date) => api.get('/leaves/admin/jfr-candidates/' + date).then(r => r.data),
  // (voir aussi usersApi.uploadCi — photos carte d'identité)
  // userIds: crédit sélectif personne-par-personne (Florent 18/08); sans userIds → masse (legacy)
  jfrCredit: (date, userIds) => api.post('/leaves/admin/jfr-credit/' + date, userIds ? { userIds } : {}).then(r => r.data),
};

export const leaveBalances = {
  // Admin
  getForUser: (userId, year) =>
    api.get('/leaves/admin/balance/' + userId + '/' + year).then(r => r.data),
  setForUser: (userId, year, quotas) =>
    api.put('/leaves/admin/balance/' + userId + '/' + year, quotas).then(r => r.data),
  // Bulk: all users + balances + status counts (Day 9 — Soldes par utilisateur tab)
  listAllUsers: (year, includeInactive = false) =>
    api.get('/leaves/admin/user-balances/' + year, { params: { includeInactive } }).then(r => r.data),
};

// ---------------------------------------------------------------------
// Timesheets Global (Dashboard ADMIN 11-05-2026)
// ---------------------------------------------------------------------
// ---------------------------------------------------------------------
// Errors (M-006 — 29-05-2026)
// ---------------------------------------------------------------------
export const errors = {
  // Typologies
  listTypologies: (activeOnly = true) =>
    api.get('/errors/typologies', { params: { activeOnly: activeOnly ? '1' : '0' } }).then(r => r.data),
  createTypology: (payload) => api.post('/errors/typologies', payload).then(r => r.data),
  updateTypology: (id, payload) => api.patch(`/errors/typologies/${id}`, payload).then(r => r.data),
  deleteTypology: (id) => api.delete(`/errors/typologies/${id}`).then(r => r.data),
  // List
  listMe:    (params = {}) => api.get('/errors/me', { params }).then(r => r.data),
  listAll:   (params = {}) => api.get('/errors', { params }).then(r => r.data),
  stats:     (params = {}) => api.get('/errors/stats', { params }).then(r => r.data),
  // Badge
  myUnseenCount: () => api.get('/errors/my-unseen-count').then(r => r.data),
  markSeen:      () => api.post('/errors/mark-seen').then(r => r.data),
  // CRUD
  create: (payload) => api.post('/errors', payload).then(r => r.data),
  update: (id, payload) => api.patch(`/errors/${id}`, payload).then(r => r.data),
  delete: (id) => api.delete(`/errors/${id}`).then(r => r.data),
};

// ---------------------------------------------------------------------
// Facturation (M-007 — 29-05-2026)
// ---------------------------------------------------------------------
export const facturation = {
  overview: (yearMonth) => api.get(`/facturation/overview/${yearMonth}`).then(r => r.data),
  client:   (yearMonth, projectId) => api.get(`/facturation/client/${yearMonth}/${projectId}`).then(r => r.data),
  lock:     (yearMonth) => api.post(`/facturation/lock/${yearMonth}`).then(r => r.data),
  unlock:   (yearMonth, reason) => api.post(`/facturation/unlock/${yearMonth}`, { reason }).then(r => r.data),
  drilldown: (yearMonth, kind, scope = 'all') => api.get(`/facturation/drilldown/${yearMonth}/${kind}/${scope}`).then(r => r.data),
  approveTs: (entryId) => api.post(`/facturation/ts/${entryId}/approve`).then(r => r.data),
  rejectTs:  (entryId, reason) => api.post(`/facturation/ts/${entryId}/reject`, { reason }).then(r => r.data),
  deleteTs:  (entryId) => api.delete(`/facturation/ts/${entryId}`).then(r => r.data),
  exportExcelUrl: (yearMonth) => `/api/facturation/export/${yearMonth}`,
};

export const timesheetsGlobal = {
  getGrid: (from, to, includeMasked = false) =>
    api.get('/timesheets-global', { params: { from, to, ...(includeMasked ? { includeMasked: 'true' } : {}) } }).then(r => r.data),
  getDay: (userId, date) =>
    api.get('/timesheets-global/day', { params: { userId, date } }).then(r => r.data),
  // Day 11 (28-05-2026) — admin edit/delete entries from popup
  updateEntry: (id, payload) =>
    api.patch(`/timesheets-global/entry/${id}`, payload).then(r => r.data),
  deleteEntry: (id) =>
    api.delete(`/timesheets-global/entry/${id}`).then(r => r.data),
};

// ---------------------------------------------------------------------
// Permissions (Faza 3.7 - 12-05-2026)
// SUPER_ADMIN only
// ---------------------------------------------------------------------
export const permissions = {
  getMatrix: () =>
    api.get('/permissions/matrix').then(r => r.data),
  // Les onglets « Matrice » et « Overrides individuels » ont été supprimés
  // le 30-09-2026 : plus aucun écran n'appelait role-grant / role-revoke /
  // override. Les routes existent toujours côté serveur (elles n'ont pas été
  // touchées) ; c'est l'enveloppe inutilisée qui part, pour qu'on ne croie
  // pas qu'un écran s'en sert encore.
  getAudit: (params) =>
    api.get('/permissions/audit', { params }).then(r => r.data),

  // --- Écran « Modules » (30-09-2026) --------------------------------
  // getSelectionSources : rôles + groupes + personnes pour la colonne « Qui ».
  // getModulesView      : POST et non GET — une sélection peut porter 100+
  //                       identifiants, ce qui ne tient pas dans une URL.
  // bulkApply           : dryRun=true renvoie le plan (écran de confirmation),
  //                       dryRun=false l'applique.
  getSelectionSources: () =>
    api.get('/permissions/selection-sources').then(r => r.data),
  getModulesView: ({ userIds = [], groupIds = [], roleIds = [] }) =>
    api.post('/permissions/modules-view', { userIds, groupIds, roleIds }).then(r => r.data),
  bulkApply: (payload) =>
    api.post('/permissions/bulk-apply', payload).then(r => r.data),
};


// ---------------------------------------------------------------------
// Supply Types (M-001 - 13-05-2026) - Catalog de tipuri de fournitures
// ---------------------------------------------------------------------
export const supplyTypes = {
  list: (params) => api.get('/supply-types', { params }).then(r => r.data),
  get: (id) => api.get(`/supply-types/${id}`).then(r => r.data),
  create: (data) => api.post('/supply-types', data).then(r => r.data),
  update: (id, data) => api.put(`/supply-types/${id}`, data).then(r => r.data),
  remove: (id, params) => api.delete(`/supply-types/${id}`, { params }).then(r => r.data),
};

// ---------------------------------------------------------------------
// Supply Draws (M-001 - 13-05-2026) - Prelevements fournitures
// + Day 3 (20-05-2026): bulkInvoice / bulkUninvoice
// ---------------------------------------------------------------------
export const supplyDraws = {
  list: (params) => api.get('/supply-draws', { params }).then(r => r.data),
  get: (id) => api.get(`/supply-draws/${id}`).then(r => r.data),
  create: (data) => api.post('/supply-draws', data).then(r => r.data),
  update: (id, data) => api.put(`/supply-draws/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/supply-draws/${id}`).then(r => r.data),
  toggleInvoice: (id, invoiced) =>
    api.post(`/supply-draws/${id}/invoice`, { invoiced }).then(r => r.data),
  nextRef: () => api.get('/supply-draws/next-ref').then(r => r.data),
  // Day 3 NEW - bulk operations
  bulkInvoice:   (body) => api.post('/supply-draws/bulk-invoice', body).then(r => r.data),
  bulkUninvoice: (body) => api.post('/supply-draws/bulk-uninvoice', body).then(r => r.data),
};



// ---------------------------------------------------------------------
// Printing Clients (M-003 - 13-05-2026) - Catalog clienti printing
// ---------------------------------------------------------------------
export const printingClients = {
  list: (params) => api.get('/printing-clients', { params }).then(r => r.data),
  get: (id) => api.get(`/printing-clients/${id}`).then(r => r.data),
  create: (data) => api.post('/printing-clients', data).then(r => r.data),
  update: (id, data) => api.patch(`/printing-clients/${id}`, data).then(r => r.data),
  remove: (id, params) => api.delete(`/printing-clients/${id}`, { params }).then(r => r.data),
};

// ---------------------------------------------------------------------
// Printing Task Templates (M-003 - 13-05-2026) - Catalog taches printing
// ---------------------------------------------------------------------
export const printingTaskTemplates = {
  list: (params) => api.get('/printing-task-templates', { params }).then(r => r.data),
  get: (id) => api.get(`/printing-task-templates/${id}`).then(r => r.data),
  create: (data) => api.post('/printing-task-templates', data).then(r => r.data),
  update: (id, data) => api.patch(`/printing-task-templates/${id}`, data).then(r => r.data),
  remove: (id, params) => api.delete(`/printing-task-templates/${id}`, { params }).then(r => r.data),
};

// ---------------------------------------------------------------------
// Printing Jobs (M-003 - 13-05-2026) - Jobs d'impression
// + Day 3 (20-05-2026): bulkInvoice / bulkUninvoice
// ---------------------------------------------------------------------
export const printingJobs = {
  list: (params) => api.get('/printing-jobs', { params }).then(r => r.data),
  get: (id) => api.get(`/printing-jobs/${id}`).then(r => r.data),
  create: (data) => api.post('/printing-jobs', data).then(r => r.data),
  update: (id, data) => api.patch(`/printing-jobs/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/printing-jobs/${id}`).then(r => r.data),
  toggleInvoice: (id, invoiced) =>
    api.post(`/printing-jobs/${id}/invoice`, { invoiced }).then(r => r.data),
  nextRef: () => api.get('/printing-jobs/next-ref').then(r => r.data),
  // Day 3 NEW - bulk operations
  bulkInvoice:   (body) => api.post('/printing-jobs/bulk-invoice', body).then(r => r.data),
  bulkUninvoice: (body) => api.post('/printing-jobs/bulk-uninvoice', body).then(r => r.data),
};


// ---------------------------------------------------------------------
// Vehicles (Day 11 - 28-05-2026) - Parc de véhicules pour Trajets
// ---------------------------------------------------------------------
export const vehicles = {
  list: (params) => api.get('/vehicles', { params }).then(r => r.data),
  create: (data) => api.post('/vehicles', data).then(r => r.data),
  update: (id, data) => api.patch(`/vehicles/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/vehicles/${id}`).then(r => r.data),
};

// ---------------------------------------------------------------------
// Cahiers de charge (Day 11 - 28-05-2026) - Fichiers attachés par projet
// ---------------------------------------------------------------------
export const cahiers = {
  listProjects: () => api.get('/cahiers').then(r => r.data),
  listFiles: (projectId) => api.get(`/cahiers/project/${projectId}`).then(r => r.data),
  upload: (projectId, formData) => api.post(`/cahiers/project/${projectId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data),
  download: (fileId, inline = false) => api.get(`/cahiers/${fileId}/download`, {
    params: inline ? { inline: '1' } : {},
    responseType: 'blob',
  }),
  remove: (fileId) => api.delete(`/cahiers/${fileId}`).then(r => r.data),
};

// ---------------------------------------------------------------------
// Trip Carriers (M-002 - 18-05-2026) - Catalog transporteurs
// ---------------------------------------------------------------------
export const tripCarriers = {
  list: (params) => api.get('/trip-carriers', { params }).then(r => r.data),
  get: (id) => api.get(`/trip-carriers/${id}`).then(r => r.data),
  create: (data) => api.post('/trip-carriers', data).then(r => r.data),
  update: (id, data) => api.patch(`/trip-carriers/${id}`, data).then(r => r.data),
  remove: (id, params) => api.delete(`/trip-carriers/${id}`, { params }).then(r => r.data),
};

// ---------------------------------------------------------------------
// Trip Task Templates (M-002 - 18-05-2026) - Catalog taches par projet
// ---------------------------------------------------------------------
export const tripTaskTemplates = {
  list: (params) => api.get('/trip-task-templates', { params }).then(r => r.data),
  get: (id) => api.get(`/trip-task-templates/${id}`).then(r => r.data),
  create: (data) => api.post('/trip-task-templates', data).then(r => r.data),
  update: (id, data) => api.patch(`/trip-task-templates/${id}`, data).then(r => r.data),
  remove: (id, params) => api.delete(`/trip-task-templates/${id}`, { params }).then(r => r.data),
};

// ---------------------------------------------------------------------
// Trips (M-002 - 18-05-2026) - Dispatch + driver self-complete
// + Day 3 (20-05-2026): invoice / uninvoice / bulkInvoice / bulkUninvoice
// ---------------------------------------------------------------------
export const trips = {
  list: (params) => api.get('/trips', { params }).then(r => r.data),
  get: (id) => api.get(`/trips/${id}`).then(r => r.data),
  create: (data) => api.post('/trips', data).then(r => r.data),
  update: (id, data) => api.patch(`/trips/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/trips/${id}`).then(r => r.data),
  complete: (id, data) => api.post(`/trips/${id}/complete`, data).then(r => r.data),
  nextRef: () => api.get('/trips/next-ref').then(r => r.data),
  // Day 3 NEW - invoice flow (pattern M-004/M-005)
  invoice:        (id) => api.post(`/trips/${id}/invoice`).then(r => r.data),
  uninvoice:      (id) => api.post(`/trips/${id}/uninvoice`).then(r => r.data),
  bulkInvoice:   (body) => api.post('/trips/bulk-invoice', body).then(r => r.data),
  bulkUninvoice: (body) => api.post('/trips/bulk-uninvoice', body).then(r => r.data),
};


// ---------------------------------------------------------------------
// Inventory Items (M-004 - 19-05-2026) - Catalog items inventaire
// ---------------------------------------------------------------------
export const inventoryItems = {
  list: (params) => api.get('/inventory-items', { params }).then(r => r.data),
  get: (id) => api.get(`/inventory-items/${id}`).then(r => r.data),
  create: (data) => api.post('/inventory-items', data).then(r => r.data),
  update: (id, data) => api.patch(`/inventory-items/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/inventory-items/${id}`).then(r => r.data),
};

// ---------------------------------------------------------------------
// Inventory Counts (M-004 - 19-05-2026) - Comptages bilunaires
// ---------------------------------------------------------------------
export const inventoryCounts = {
  list: (params) => api.get('/inventory-counts', { params }).then(r => r.data),
  get: (id) => api.get(`/inventory-counts/${id}`).then(r => r.data),
  create: (data) => api.post('/inventory-counts', data).then(r => r.data),
  update: (id, data) => api.patch(`/inventory-counts/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/inventory-counts/${id}`).then(r => r.data),
  invoice:        (id) => api.post(`/inventory-counts/${id}/invoice`).then(r => r.data),
  uninvoice:      (id) => api.post(`/inventory-counts/${id}/uninvoice`).then(r => r.data),
  bulkInvoice:   (body) => api.post('/inventory-counts/bulk-invoice', body).then(r => r.data),
  bulkUninvoice: (body) => api.post('/inventory-counts/bulk-uninvoice', body).then(r => r.data),
};

// ---------------------------------------------------------------------
// Pallet Types (M-005 - 19-05-2026) - Catalog tipuri paleti
// ---------------------------------------------------------------------
export const palletTypes = {
  list: (params) => api.get('/pallet-types', { params }).then(r => r.data),
  get: (id) => api.get(`/pallet-types/${id}`).then(r => r.data),
  create: (data) => api.post('/pallet-types', data).then(r => r.data),
  update: (id, data) => api.patch(`/pallet-types/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/pallet-types/${id}`).then(r => r.data),
};

// ---------------------------------------------------------------------
// Pallet Movements (M-005 - 19-05-2026) - Suivi miscari paleti + facture toggle
// ---------------------------------------------------------------------
export const palletMovements = {
  list: (params) => api.get('/pallet-movements', { params }).then(r => r.data),
  get: (id) => api.get(`/pallet-movements/${id}`).then(r => r.data),
  create: (data) => api.post('/pallet-movements', data).then(r => r.data),
  update: (id, data) => api.patch(`/pallet-movements/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/pallet-movements/${id}`).then(r => r.data),
  invoice:   (id) => api.post(`/pallet-movements/${id}/invoice`).then(r => r.data),
  uninvoice: (id) => api.post(`/pallet-movements/${id}/uninvoice`).then(r => r.data),
  bulkInvoice:   (body) => api.post('/pallet-movements/bulk-invoice', body).then(r => r.data),
  bulkUninvoice: (body) => api.post('/pallet-movements/bulk-uninvoice', body).then(r => r.data),
};

// ---------------------------------------------------------------------
// Convoyeur (M-008 - 24-06-2026) - Proxy vers l'edge (tri colis)
// ---------------------------------------------------------------------
export const convoyeur = {
  status:        () => api.get('/convoyeur/status').then(r => r.data),
  search:        (by, q) => api.get('/convoyeur/search', { params: { by, q } }).then(r => r.data),
  lineStats:     () => api.get('/convoyeur/line-stats').then(r => r.data),
  journal:       (params) => api.get('/convoyeur/journal', { params }).then(r => r.data),
  reconcile:     () => api.get('/convoyeur/reconcile').then(r => r.data),
  reconcileDetail: (carrier, status) =>
    api.get('/convoyeur/reconcile/detail', { params: { carrier, status } }).then(r => r.data),
  robotStatus:   () => api.get('/convoyeur/robot/status').then(r => r.data),
  robotRun:      () => api.post('/convoyeur/robot/run').then(r => r.data),
  // Photo : <img src> direct (cookie httpOnly envoyé same-origin)
  imageUrl:      (code) => `/api/convoyeur/image?code=${encodeURIComponent(code)}`,
};

// M-009 (20-08-2026) — Dimensions & poids UM → export SFTP EffiTrace
export const umMeasures = {
  list:       (params) => api.get('/um-measures', { params }).then(r => r.data),
  flowStatus: () => api.get('/um-measures/flow-status').then(r => r.data),
  findByCode: (code) => api.get('/um-measures/find-by-code', { params: { code } }).then(r => r.data),
  create:     (data) => api.post('/um-measures', data).then(r => r.data),
  update:     (id, data) => api.put(`/um-measures/${id}`, data).then(r => r.data),
  remove:     (id) => api.delete(`/um-measures/${id}`).then(r => r.data),
  exportPreview: () => api.get('/um-measures/export/preview').then(r => r.data),
  exportRun:  () => api.post('/um-measures/export/run').then(r => r.data),
  // Photos palettes/colis (20-08-2026) — stockées pour envoi futur aux clients
  uploadPhoto: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/um-measures/${id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  photoUrl:   (id, photoId) => `/api/um-measures/${id}/photos/${photoId}`,
  deletePhoto: (id, photoId) => api.delete(`/um-measures/${id}/photos/${photoId}`).then(r => r.data),
  // Mail client par commande (20-08-2026)
  sendMail:   (id) => api.post(`/um-measures/${id}/send-mail`).then(r => r.data),
  clients:    (all) => api.get('/um-measures/clients', { params: all ? { all: '1' } : {} }).then(r => r.data),
  clientCreate: (data) => api.post('/um-measures/clients', data).then(r => r.data),
  clientUpdate: (id, data) => api.put(`/um-measures/clients/${id}`, data).then(r => r.data),
  // Palettes (23-10-2026) : étiquette Zebra par palette + BL « détail des palettes »
  palettes:   (clientId, orderRef) => api.get('/um-measures/palettes', { params: { clientId, orderRef } }).then(r => r.data),
  imprimerEtiquette: (id) => api.post(`/um-measures/${id}/etiquette`).then(r => r.data),
  imprimerBl: (clientId, orderRef) => api.post('/um-measures/bl', { clientId, orderRef }).then(r => r.data),
};

// Commande prête Nespart (13-10-2026) — unités (palettes/colis) mesurées +
// photos, mail anglais « ready for collection » (contrat : SPEC §3.5-3.6).
// Les écritures renvoient le CommandeDTO à jour (ou { dto, … }).
export const readyOrders = {
  list:      () => api.get('/ready-orders').then(r => r.data),
  // v3 (16-10-2026) — vue d'ensemble : { compteurs, envoyees, brouillons, quai, chargees } ; jour = 0 | 1 | 7.
  vue:       ({ clientId, jour } = {}) => api.get('/ready-orders/vue', { params: { ...(clientId ? { clientId } : {}), jour: jour ?? 0 } }).then(r => r.data),
  // v3 — admin : reprendre la facturation automatique (filmage, palettes sorties) → { avertissements, facturation }
  reessayerFacturation: (id) => api.post(`/ready-orders/${encodeURIComponent(id)}/facturation/reessayer`).then(r => r.data),
  // v3 — admin : temps d'encodage par utilisateur (du / au : AAAA-MM-JJ) — panneau « ⏱ Temps d'encodage » (27-10-2026)
  statsEncodage: (du, au) => api.get('/ready-orders/stats/encodage', { params: { ...(du ? { du } : {}), ...(au ? { au } : {}) } }).then(r => r.data),
  exportEncodage: (du, au) => api.get('/ready-orders/stats/encodage/export', { params: { ...(du ? { du } : {}), ...(au ? { au } : {}) }, responseType: 'blob', timeout: 120000 }),
  // 27-10-2026 — admin : temps FACTURABLE d'une anomalie (minutes entières ≤ temps mesuré, motif obligatoire)
  corrigerEncodage: (id, minutes, motif) => api.post(`/ready-orders/${encodeURIComponent(id)}/encodage/correction`, { minutes, motif }).then(r => r.data),
  // 27-10-2026 — admin : commandes sur dalle (jours, tranches, clients) + export
  statsDalle: (du, au) => api.get('/ready-orders/stats/dalle', { params: { ...(du ? { du } : {}), ...(au ? { au } : {}) } }).then(r => r.data),
  exportDalle: (du, au) => api.get('/ready-orders/stats/dalle/export', { params: { ...(du ? { du } : {}), ...(au ? { au } : {}) }, responseType: 'blob', timeout: 120000 }),
  // v2 (16-10-2026, SPEC-v2 §3) : clients actifs proposés dans « Commandes prêtes »
  // → [{ id, name, carrierCode, mailLanguages, readyEnabled }] (tri alphabétique).
  clients:   () => api.get('/ready-orders/clients').then(r => r.data),
  // Une commande par (client, réf.) : rouverte, jamais créée deux fois. clientId obligatoire.
  // (17/10) La réf. a aussi une commande de TEST : 409 CHOIX_TEST_OU_REELLE { choix } sans rien créer ;
  // la réponse part telle quelle (`choix`, 12e revue) : 'reelle' (la vraie commande, rouverte ou créée) |
  // 'reelle-existante' (la vraie, rouverte seulement — absente : 409 REELLE_ABSENTE) | 'essai' (la commande
  // de test, rouverte seulement — absente : 409 ESSAI_ABSENT), retrouvées par le serveur sur (client, réf.).
  create:    (orderRef, clientId, isTest = false, choix = null) =>
    api.post('/ready-orders', { orderRef, clientId, isTest: !!isTest, ...(choix ? { choix } : {}) }).then(r => r.data),
  get:       (id) => api.get(`/ready-orders/${id}`).then(r => r.data),
  patch:     (id, data) => api.patch(`/ready-orders/${id}`, data).then(r => r.data),
  // « Supprimer le brouillon » (16-10-2026) : brouillon jamais envoyé, ou commande de test (sauf pendant
  // son envoi) — sinon 409 { error, message (motif), dto } → { ok, id, orderRef, isTest }.
  remove:    (id) => api.delete(`/ready-orders/${id}`).then(r => r.data),
  setCount:  (id, kind, n) => api.put(`/ready-orders/${id}/counts`, { kind, n }).then(r => r.data),
  addUnit:   (id, kind) => api.post(`/ready-orders/${id}/units`, { kind }).then(r => r.data),
  patchUnit: (id, unitId, data) => api.patch(`/ready-orders/${id}/units/${unitId}`, data).then(r => r.data),
  // confirme : ✕ CONFIRMÉ d'une fiche que l'écran montre remplie ; sinon le serveur ne supprime
  // qu'une unité vide (409 UNITE_REMPLIE si un autre appareil l'a remplie entre-temps).
  deleteUnit: (id, unitId, { confirme = false } = {}) => api.delete(`/ready-orders/${id}/units/${unitId}`, { params: confirme ? { confirme: 1 } : undefined }).then(r => r.data),
  // 120 s : une photo part du PDA sur le wifi du dépôt, parfois faible (voir publicSubmit).
  uploadPhoto: (id, unitId, file) => {
    const fd = new FormData();
    fd.append('photo', file);
    return api.post(`/ready-orders/${id}/units/${unitId}/photos`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
    }).then(r => r.data);
  },
  // Vignette : <img src> direct (cookie httpOnly envoyé same-origin)
  photoUrl:  (id, unitId, photoId) => `/api/ready-orders/${id}/units/${unitId}/photos/${photoId}`,
  deletePhoto: (id, unitId, photoId) => api.delete(`/ready-orders/${id}/units/${unitId}/photos/${photoId}`).then(r => r.data),
  previewMail: (id) => api.get(`/ready-orders/${id}/preview-mail`).then(r => r.data),
  // 55 s : synchro EffiTrace + envoi Graph ; sous les 60 s de nginx (voir confirmInterim).
  send:      (id, expectedHash) => api.post(`/ready-orders/${id}/send`, { expectedHash }, { timeout: 55000 }).then(r => r.data),
  resolve:   (id, mailId, parti) => api.post(`/ready-orders/${id}/mails/${mailId}/resolve`, { parti }).then(r => r.data),
};

// Commandes prêtes v2 (16-10-2026) — CHARGEMENT (SPEC-v2 §3) : un départ de camion par
// client. Chaque route renvoie le LoadingDTO à jour (erreurs : { error, message, dto? }).
export const readyLoadings = {
  // Ouvre un départ (ou rouvre celui déjà ouvert pour ce client et cet utilisateur).
  // v3 (décision Silviu 16/10) : plus de client au départ — { truck? } (clientId facultatif : ancien écran).
  open:      ({ truck, clientId } = {}) => api.post('/ready-orders/loadings', { ...(clientId ? { clientId } : {}), ...(truck ? { truck } : {}) }).then(r => r.data),
  get:       (lid) => api.get(`/ready-orders/loadings/${encodeURIComponent(lid)}`).then(r => r.data),
  scan:      (lid, code) => api.post(`/ready-orders/loadings/${encodeURIComponent(lid)}/scan`, { code }).then(r => r.data),
  // « Tout charger » CONFIRMÉ (29-10-2026) : les unités montrées à l'opérateur (refus si une autre est à quai)
  loadOrder: (lid, orderId, unites) => api.post(`/ready-orders/loadings/${encodeURIComponent(lid)}/load-order`, { orderId, confirme: true, unites }).then(r => r.data),
  // v3 : réf. ambiguë (plusieurs clients, ou test et réel) → la commande choisie sur la page s'ouvre dans le départ
  openOrder: (lid, orderId) => api.post(`/ready-orders/loadings/${encodeURIComponent(lid)}/open-order`, { orderId }).then(r => r.data),
  // loadedAt : l'instant du geste que l'écran montre comme le dernier — si ce n'est plus lui (scan
  // passé entre-temps), le serveur n'annule rien (409 DEJA_ANNULE).
  undo:      (lid, loadedAt) => api.post(`/ready-orders/loadings/${encodeURIComponent(lid)}/undo`, loadedAt ? { loadedAt } : {}).then(r => r.data),
  // 55 s : la fermeture peut envoyer le mail « chargée » (Graph) ; sous les 60 s de nginx.
  close:     (lid) => api.post(`/ready-orders/loadings/${encodeURIComponent(lid)}/close`, {}, { timeout: 55000 }).then(r => r.data),
};

// Commandes prêtes v2 — PARAMÈTRES CLIENT (admins + coordinateurs : commandes_pretes.admin.all).
export const readyClientSettings = {
  list:    () => api.get('/ready-orders/settings/clients').then(r => r.data),
  // v3 : projets actifs proposés pour la facturation automatique → [{ id, name }]
  projects: () => api.get('/ready-orders/settings/projects').then(r => r.data),
  create:  (data) => api.post('/ready-orders/settings/clients', data).then(r => r.data),
  update:  (id, data) => api.put(`/ready-orders/settings/clients/${encodeURIComponent(id)}`, data).then(r => r.data),
  // Aperçu avec une commande exemple : { lang } (SPEC §3) + facultatif { type: 'pret' | 'charge',
  // mailLanguages, mailTemplates, loadMailTemplates } = réglages du formulaire pas encore enregistrés.
  preview: (id, lang, extra = {}) => api.post(`/ready-orders/settings/clients/${encodeURIComponent(id)}/preview`, { lang, ...extra }).then(r => r.data),
  // NOUVEAU lien (l'ancien meurt) : { url } renvoyé UNE seule fois.
  portalCreate: (id) => api.post(`/ready-orders/settings/clients/${encodeURIComponent(id)}/portal`).then(r => r.data),
  portalDelete: (id) => api.delete(`/ready-orders/settings/clients/${encodeURIComponent(id)}/portal`).then(r => r.data),
};

// Commandes prêtes v2 — page PUBLIQUE du client (/client/<token>) : le jeton EST l'accès.
export const publicClientPortal = {
  get: (token) => api.get(`/public/client/${encodeURIComponent(token)}`).then(r => r.data),
};

// Volet PUBLIC (page /order/<token>, en anglais) : le jeton du lien EST l'accès,
// jamais de cookie requis.
export const publicReadyOrders = {
  get:      (token) => api.get(`/public/ready-orders/${encodeURIComponent(token)}`).then(r => r.data),
  photoUrl: (token, photoId) => `/api/public/ready-orders/${encodeURIComponent(token)}/photos/${encodeURIComponent(photoId)}`,
  zipUrl:   (token) => `/api/public/ready-orders/${encodeURIComponent(token)}/zip`,
};

// Commandes prêtes v3 B (16-10-2026) — rendez-vous + documents à imprimer, lien du mail (SANS compte) :
// /order/<token>/rdv et /order/<token>/documents. Le jeton EST l'accès (même jeton que les photos).
const pub = (token) => `/public/ready-orders/${encodeURIComponent(token)}`;
export const publicReadyRdvDocs = {
  rdv:        (token) => api.get(`${pub(token)}/rdv`).then(r => r.data),
  prendreRdv: (token, data) => api.post(`${pub(token)}/rdv`, data).then(r => r.data),
  documents:  (token) => api.get(`${pub(token)}/documents`).then(r => r.data),
  // UN fichier par requête, envoyé BRUT (≤ 15 Mo ; nom dans X-File-Name) ; 300 s : 15 Mo sur un wifi de
  // chantier — un délai trop court ferait renvoyer un fichier pourtant reçu.
  televerser: (token, file) => api.post(`${pub(token)}/documents/fichiers`, file, {
    headers: { 'Content-Type': 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name || '') }, timeout: 300000,
  }).then(r => r.data),
  retirer:    (token, id) => api.delete(`${pub(token)}/documents/fichiers/${encodeURIComponent(id)}`).then(r => r.data),
  envoyer:    (token, data) => api.post(`${pub(token)}/documents`, data).then(r => r.data),
};

// Commandes prêtes v3 B — côté ZEUS : pastille, rendez-vous, documents à imprimer (mêmes droits que le module).
export const readyRdvDocs = {
  compteurs:  () => api.get('/ready-orders/rdv-docs/compteurs').then(r => r.data),
  aTraiter:   () => api.get('/ready-orders/rdv-docs/a-traiter').then(r => r.data),
  commande:   (orderId) => api.get(`/ready-orders/rdv-docs/commande/${encodeURIComponent(orderId)}`).then(r => r.data),
  vu:         (id) => api.post(`/ready-orders/rdv-docs/rdv/${encodeURIComponent(id)}/vu`).then(r => r.data),
  imprime:    (id, imprime = true) => api.post(`/ready-orders/rdv-docs/documents/${encodeURIComponent(id)}/imprime`, { imprime }).then(r => r.data),
  // v3 C (17-10-2026) « 🖨 Imprimer tout » : file d'impression lue par l'agent de server-home (Zebra / Konica).
  // documentIds (facultatif) : seulement ceux-là (la liste « à traiter » envoie ce qu'elle affiche).
  imprimerTout: (orderId, documentIds) => api.post(`/ready-orders/rdv-docs/commande/${encodeURIComponent(orderId)}/imprimer-tout`, documentIds ? { documentIds } : {}).then(r => r.data),
  reimprimer: (id) => api.post(`/ready-orders/rdv-docs/documents/${encodeURIComponent(id)}/imprimer`).then(r => r.data),
  etatImpression: () => api.get('/ready-orders/rdv-docs/impression/etat').then(r => r.data),
  // PDF / images : ouverts dans un nouvel onglet (lien `url` du document, impression Windows) ; ZPL :
  // OCTETS (ArrayBuffer, jamais décodés) pour le pont d'impression local.
  // 120 s : jusqu'à 15 Mo sur le wifi du dépôt ; une erreur (JSON reçue en octets) est relue pour son message.
  octetsZpl:  (id) => api.get(`/ready-orders/rdv-docs/documents/${encodeURIComponent(id)}/fichier`, { params: { brut: 1 }, responseType: 'arraybuffer', timeout: 120000 })
    .then(r => r.data)
    .catch((e) => {
      const d = e?.response?.data;
      if (d instanceof ArrayBuffer) { try { e.response.data = JSON.parse(new TextDecoder().decode(d)); } catch (_x) { /* pas du JSON */ } }
      throw e;
    }),
};

// M-010 (25-08-2026) — Registre des premiers soins et des accidents.
// Le scan d'un QR sert deux flux : vérification périodique de l'objet
// (art. I.5-4) et déclaration d'un accident (art. I.5-6 §3).
export const safety = {
  scan:        (code) => api.get(`/safety-incidents/scan/${encodeURIComponent(code)}`).then(r => r.data),
  firstAiders: () => api.get('/safety-incidents/first-aiders').then(r => r.data),
  contexteDeclaration: () => api.get('/safety-incidents/contexte-declaration').then(r => r.data),
  people:      () => api.get('/safety-incidents/people').then(r => r.data),
  vocab:       () => api.get('/safety-incidents/vocab').then(r => r.data),
  sos:         (data) => api.post('/safety-incidents/sos', data).then(r => r.data),
  declare:     (data) => api.post('/safety-incidents', data).then(r => r.data),
  // 23-10-2026 : un SOS se complète (au lieu de créer une seconde fiche)
  completeSos: (id, data) => api.post(`/safety-incidents/${id}/completer`, data).then(r => r.data),
  list:        (params) => api.get('/safety-incidents', { params }).then(r => r.data),
  get:         (id) => api.get(`/safety-incidents/${id}`).then(r => r.data),
  deadlines:   () => api.get('/safety-incidents/deadlines').then(r => r.data),
  qualify:     (id, data) => api.post(`/safety-incidents/${id}/qualify`, data).then(r => r.data),
  aggravate:   (id, data) => api.post(`/safety-incidents/${id}/aggravate`, data).then(r => r.data),
  insurer:     (id, data) => api.post(`/safety-incidents/${id}/insurer`, data).then(r => r.data),
  reportSent:  (id, data) => api.post(`/safety-incidents/${id}/report-sent`, data).then(r => r.data),
  inspection:  (id, data) => api.post(`/safety-incidents/${id}/inspection`, data).then(r => r.data),
  close:       (id, data) => api.post(`/safety-incidents/${id}/close`, data).then(r => r.data),
  // 23-10-2026 : l'écran « Secouristes » — qui est formé, jusqu'à quand
  secouristes:        () => api.get('/safety-incidents/secouristes').then(r => r.data),
  ajouterSecouriste:  (data) => api.post('/safety-incidents/secouristes', data).then(r => r.data),
  modifierSecouriste: (id, data) => api.put(`/safety-incidents/secouristes/${id}`, data).then(r => r.data),
  retirerSecouriste:  (id, motif) => api.post(`/safety-incidents/secouristes/${id}/retirer`, { motif }).then(r => r.data),
  certificatUrl:      (id) => `/api/safety-incidents/secouristes/${id}/certificat`,
  envoyerCertificat:  (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/safety-incidents/secouristes/${id}/certificat`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
};

// M-010 — le plan du site : pastilles deplacables, objets ajoutables.
// L'image passe par l'API (et non par /public) : un plan de site montre les
// sorties de secours et l'implantation, ce n'est pas public.
export const safetyPlans = {
  list:       () => api.get('/safety-plans').then(r => r.data),
  imageUrl:   (planId) => `/api/safety-plans/${planId}/image`,
  assets:     (planId) => api.get(`/safety-plans/${planId}/assets`).then(r => r.data),
  allAssets:  (kinds) => api.get('/safety-plans/assets',
    { params: kinds && kinds.length ? { kinds: kinds.join(',') } : {} }).then(r => r.data),
  unplaced:   () => api.get('/safety-plans/assets/unplaced').then(r => r.data),
  setPosition: (assetId, data) => api.patch(`/safety-plans/assets/${assetId}/position`, data).then(r => r.data),
  unplace:    (assetId) => api.delete(`/safety-plans/assets/${assetId}/position`).then(r => r.data),
  createAsset: (data) => api.post('/safety-plans/assets', data).then(r => r.data),
};

// M-010 — actions correctives. Un rack plie n'est PAS un accident : il n'a
// pas de victime, donc il n'entre pas au registre des premiers soins.
export const safetyActions = {
  vocab:  () => api.get('/safety-actions/vocab').then(r => r.data),
  list:   (params) => api.get('/safety-actions', { params }).then(r => r.data),
  report: (data) => api.post('/safety-actions', data).then(r => r.data),
  update: (id, data) => api.patch(`/safety-actions/${id}`, data).then(r => r.data),
  close:  (id, data) => api.post(`/safety-actions/${id}/close`, data).then(r => r.data),
};

// M-010 — check-lists. Une ligne NOK ouvre automatiquement une action
// corrective cote serveur : le frontend n'a rien a orchestrer.
// 23-10-2026 : la tournée se fait PAR VÉRIFICATION (une ou plusieurs personnes chacune),
// les procédures se règlent à l'écran (droit maintenance.configure.all), le temps va dans les tâches.
export const safetyRoutines = {
  templates:   () => api.get('/safety-routines/templates').then(r => r.data),
  occurrences: (params) => api.get('/safety-routines/occurrences', { params }).then(r => r.data),
  occurrence:  (id) => api.get(`/safety-routines/occurrences/${id}`).then(r => r.data),
  week:        (isoWeek) => api.get('/safety-routines/week', { params: isoWeek ? { isoWeek } : {} }).then(r => r.data),
  debrief:     (data) => api.post('/safety-routines/week/debrief', data).then(r => r.data),
  // procédures
  procedures:  () => api.get('/safety-routines/procedures').then(r => r.data),
  updateItem:  (id, data) => api.put(`/safety-routines/procedures/items/${id}`, data).then(r => r.data),
  createItem:  (templateId, data) => api.post(`/safety-routines/procedures/${templateId}/items`, data).then(r => r.data),
  reorder:     (templateId, itemIds) => api.put(`/safety-routines/procedures/${templateId}/ordre`, { itemIds }).then(r => r.data),
  // la tournée de Vincent
  tournees:    () => api.get('/safety-routines/tournees').then(r => r.data),
  attribution: (id) => api.get(`/safety-routines/occurrences/${id}/attribution`).then(r => r.data),
  saveAttribution: (id, data) => api.put(`/safety-routines/occurrences/${id}/attribution`, data).then(r => r.data),
  // mes vérifications
  mesVerifications: () => api.get('/safety-routines/mes-verifications').then(r => r.data),
  commencer:   (id, itemId) => api.post(`/safety-routines/occurrences/${id}/lignes/${itemId}/commencer`).then(r => r.data),
  terminer:    (id, itemId, data) => api.post(`/safety-routines/occurrences/${id}/lignes/${itemId}`, data).then(r => r.data),
};

// M-010 - echeancier des controles legaux (onglet 7). Leur en-tete le dit :
// « le premier document que demande l'inspection ». Ce client sert a ce que
// ce document soit vivant, pas recopie.
export const safetyLegal = {
  list:     () => api.get('/safety-legal').then(r => r.data),
  statuses: () => api.get('/safety-legal/statuses').then(r => r.data),
  update:   (id, data) => api.patch(`/safety-legal/${id}`, data).then(r => r.data),
};

// M-010 - le bandeau permanent. Le courriel previent, le bandeau insiste :
// il reste affiche tant que l'action n'est pas faite et n'a pas de bouton
// « fermer », donc rien a cliquer pour l'ignorer.
export const safetyAlerts = {
  mine: () => api.get('/safety-alerts/mine').then(r => r.data),
};

// M-011 (26-08-2026) — Onboarding contrats par QR.
// Volet admin (invitations + validation) + volet PUBLIC (page /embauche/<token>,
// sans session — le candidat n'a pas encore de compte).
export const onboarding = {
  list:     () => api.get('/onboarding').then(r => r.data),
  create:   (data) => api.post('/onboarding', data).then(r => r.data),
  // Prolongation d'un collaborateur connu (30-09-2026) : pas de QR, mails directs
  prolongation: (data) => api.post('/onboarding/prolongation', data).then(r => r.data),
  interimRequest: (data) => api.post('/onboarding/interim', data).then(r => r.data),
  // persons : [{ name, decision?: { type: 'reprendre', userId, vus } | { type: 'nouveau', vus } }]
  // vus = [{ id, reprenable, exact, memeOrdre, aDossier? }] des comptes montrés pour ce nom
  // (un compte non vu, devenu reprenable, ou — pour une reprise — plus « exact », plus dans
  // le même ordre ou avec des données personnelles, repose la question).
  // options.doublonsAcceptes : clés (clePaire) des paires confirmées.
  // 55 s : la transaction et la réparation des droits de base peuvent attendre une base
  // occupée au-delà des 15 s par défaut ; sous les 60 s de nginx (voir fullExtract).
  confirmInterim: (id, persons, options = {}) => api.post(`/onboarding/${id}/confirm-interim`, { persons, ...options }, { timeout: 55000 }).then(r => r.data),
  validate: (id) => api.post(`/onboarding/${id}/validate`).then(r => r.data),
  refuse:   (id, reason) => api.post(`/onboarding/${id}/refuse`, { reason }).then(r => r.data),
  cancel:   (id) => api.delete(`/onboarding/${id}`).then(r => r.data),
  ciUrl:    (id, side) => `/api/onboarding/${id}/ci/${side}`,
  // Public — token dans l'URL, jamais de cookie requis
  publicGet:    (token) => api.get(`/public/onboarding/${token}`).then(r => r.data),
  // 180 s : le candidat envoie depuis son téléphone, souvent en 4G faible. Le délai
  // compte la durée de l'envoi des photos ; nginx, lui, ne coupe pas un envoi lent
  // tant qu'il progresse (client_body_timeout = entre deux paquets).
  publicSubmit: (token, formData) =>
    api.post(`/public/onboarding/${token}/submit`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000,
    }).then(r => r.data),
  // Prolongation : confirmation 1 clic, aucun formulaire
  publicConfirm: (token) => api.post(`/public/onboarding/${token}/confirm`).then(r => r.data),
};

// M-015 (29-08-2026) — Remise transporteur : bordereaux EffiTrace + signature.
// La synchronisation interroge EffiTrace page par page : elle est lente par
// nature (30 s pour 2 jours tous transporteurs), d'ou le delai eleve.
export const bordereaux = {
  carriers: () => api.get('/bordereaux/carriers').then(r => r.data),
  sync: (carrierCode, days = 3) =>
    api.post('/bordereaux/sync', { carrierCode, days }, { timeout: 180000 }).then(r => r.data),
  pending: (carrierCode) =>
    api.get('/bordereaux/pending', { params: carrierCode ? { carrierCode } : {} }).then(r => r.data),
  handover: (payload) =>
    api.post('/bordereaux/handover', payload, { timeout: 120000 }).then(r => r.data),
  archive: (params) => api.get('/bordereaux/archive', { params }).then(r => r.data),
  pdfUrl: (id, original = false) => `/api/bordereaux/${id}/pdf${original ? '?original=1' : ''}`,
  carrier: (code) => api.get(`/bordereaux/carrier/${code}`, { timeout: 60000 }).then(r => r.data),
  available: (detail = false) =>
    api.get('/bordereaux/available', { params: detail ? { detail: 1 } : {}, timeout: 60000 }).then(r => r.data),
  handover1: (id) => api.get(`/bordereaux/handover/${id}`).then(r => r.data),
  cancelHandover: (id, reason) =>
    api.post(`/bordereaux/handover/${id}/cancel`, { reason }, { timeout: 175000 }).then(r => r.data),
  proofUrl: (id) => `/api/bordereaux/handover/${id}/proof.pdf`,
  allUrl: (id) => `/api/bordereaux/handover/${id}/all.pdf`,
  print: (id, mode) => api.post(`/bordereaux/handover/${id}/print`, { mode }, { timeout: 120000 }).then(r => r.data),
  // bordereaux automatiques (27-10-2026)
  auto: () => api.get('/bordereaux/auto').then(r => r.data),
  autoColis: (force = false) => api.get('/bordereaux/auto/colis', { params: force ? { force: 1 } : {}, timeout: 180000 }).then(r => r.data),
  autoRegler: (code, reglage) => api.put(`/bordereaux/auto/${code}`, reglage).then(r => r.data),
  autoLancer: (corps) => api.post('/bordereaux/auto/lancer', corps, { timeout: 180000 }).then(r => r.data),
  // annuler des bordereaux NON signés (28-10-2026) ; remises signées du jour (annulables) d'un transporteur
  annuler: (ids, raison) => api.post('/bordereaux/annuler', { ids, raison }, { timeout: 180000 }).then(r => r.data),
  // sortir SANS signature du chauffeur (28-10-2026, temporaire) : une seule sortie EffiTrace pour tout ce qui est coché
  sortirSansSignature: (ids, raison) => api.post('/bordereaux/sortir-sans-signature', { ids, raison }, { timeout: 180000 }).then(r => r.data),
  aControler: (carrierCode) => api.get('/bordereaux/annulations-a-controler', { params: { carrierCode } }).then(r => r.data),
  verdictAnnulation: (id, verdict) => api.post(`/bordereaux/${id}/verdict-annulation`, { verdict }).then(r => r.data),
  remisesDuJour: (carrierCode) => api.get('/bordereaux/remises-du-jour', { params: { carrierCode } }).then(r => r.data),
  autoExecution: (carrierCode, depuis) => api.get('/bordereaux/auto/execution', { params: { carrierCode, depuis } }).then(r => r.data),
};

export default api;
